import CondolencesIcon from '@/assets/CondolencesIcon'
import FsnNetworkIcon from '@/assets/FsnNetworkIcon'
import ReportDateRangeFilterBar, {
  type ReportDateRangeSelection,
} from '@/components/reports/ReportDateRangeFilterBar'
import { AdminHeaderActionButton, AdminPageHeader } from '@/components/patterns/admin-page'
import { EmptyState } from '@/components/patterns/empty-state'
import { InfoCard, type InfoCardProps } from '@/components/contracts/InfoCard'
import { IndexDataTable } from '@/components/tables/IndexTable'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Typography } from '@/components/ui/typography'
import { usePathHelper, type PathLink } from '@/hooks/usePathHelper'
import { Can } from '@/lib/casl'
import {
  REPORT_DATE_MODE,
  getCurrentReportDateFilters,
  getOrganizationReportDateBounds,
} from '@/lib/report-date-range'
import {
  organizationQueries,
  useOrganization,
} from '@/lib/services/organizations/organizations.query'
import {
  reportQueries,
  useExportExternalOrdersReportMutation,
  useExternalOrdersReport,
} from '@/lib/services/reports/reports.query'
import {
  externalOrdersReportSearchSchema,
  getExternalOrdersReportFiltersFromSearch,
  type ExternalOrdersReportFilters,
  type ExternalOrdersReportSearch,
} from '@/lib/services/reports/reports.schema'
import type {
  ExternalOrdersReportRow,
  ExternalOrdersReportSourceTotals,
  ExternalOrdersReportSummary,
} from '@/lib/services/reports/reports.types'
import { cn, formatCurrency, parseCurrency, readProperty, unslugify } from '@/lib/utils'
import { createFileRoute, Link, notFound, useNavigate } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { Cell, Label, Pie, PieChart, type LabelProps } from 'recharts'
import { Download, HandCoins, Inbox, Network, Settings } from 'lucide-react'
import { startTransition, type ComponentType } from 'react'
import { toast } from 'sonner'
import { downloadExportedReport, formatPieSharePercent, getExportErrorMessage } from './-helpers'
import { getOrganizationNow } from '@/lib/reporting-dates'

/** A Lucide icon or a source logo; both take a class name. */
type SourceIcon = ComponentType<{ className?: string }>

type ExternalOrderSourceMeta = {
  label: string
  shortLabel: string
  color: string
  Icon: SourceIcon
}

// Looked up by `source_system`; a source without an entry falls back to a chart color and Network.
const EXTERNAL_ORDER_SOURCE_META: Record<string, ExternalOrderSourceMeta> = {
  fsn_network: {
    label: 'Flower Shop Network',
    shortLabel: 'FSN',
    color: 'var(--color-chart-1)',
    Icon: FsnNetworkIcon,
  },
  condolences: {
    label: 'Condolences',
    shortLabel: 'Condolences',
    color: 'var(--color-chart-2)',
    Icon: CondolencesIcon,
  },
}

const FALLBACK_SOURCE_COLORS = [
  'var(--color-chart-3)',
  'var(--color-chart-4)',
  'var(--color-chart-5)',
]

/** The filters to navigate to. The source select hands back a string; `validateSearch` checks it. */
type ExternalOrdersSearchUpdate = Omit<ExternalOrdersReportFilters, 'source_system'> & {
  source_system?: string | null
}

/** One source's slice of a source mix chart. */
type SourceBreakdownRow = {
  sourceSystem: string
  label: string
  value: number
  chartKey: string
  chartValue: number
  /** The slice's fraction of the chart total. */
  share: number
  color: string | undefined
  Icon: SourceIcon
}

type SourceTotal = Pick<SourceBreakdownRow, 'sourceSystem' | 'label' | 'value'>

type FormatSourceValue = (value: number) => string

export const Route = createFileRoute('/_authenticated/$orgId/reports/external-orders')({
  component: ExternalOrdersReportPage,
  validateSearch: (search) => externalOrdersReportSearchSchema.parse(search),
  loaderDeps: ({ search }) => ({ search }),
  loader: async ({ context, deps: { search }, params }) => {
    if (!context.ability.can('list', 'transactions')) throw notFound()

    const organization = await context.queryClient.ensureQueryData(
      organizationQueries.show(params.orgId)
    )
    const filters = getReportFilters(search, organization?.timezone)

    await context.queryClient.ensureQueryData(
      reportQueries.externalOrders({ organizationId: params.orgId, filters })
    )
  },
})

export function ExternalOrdersReportPage() {
  const navigate = useNavigate()
  const path = usePathHelper()
  const { orgId } = Route.useParams()
  const search = Route.useSearch()
  const { data: organization } = useOrganization(orgId)
  const filters = getReportFilters(search, organization?.timezone)
  const { data: report } = useExternalOrdersReport(filters)
  const exportMutation = useExportExternalOrdersReportMutation()
  const integrationsLink = path.integrations.index().using({ orgId }).link
  const reportNow = getOrganizationNow(organization?.timezone)
  const datePickerBounds = getOrganizationReportDateBounds(
    organization?.created_at,
    reportNow,
    organization?.timezone
  )
  const rows = report?.rows ?? []

  const navigateWithFilters = (nextFilters: ExternalOrdersSearchUpdate, replace = true) => {
    startTransition(() => {
      void navigate({
        ...path.externalOrders.index().using({ orgId }).link,
        search: {
          start_date: nextFilters.start_date,
          end_date: nextFilters.end_date,
          date_mode: nextFilters.date_mode ?? undefined,
          source_system: nextFilters.source_system ?? undefined,
        },
        replace,
      })
    })
  }

  const applyFilters = ({ start_date, end_date, date_mode }: ReportDateRangeSelection) => {
    navigateWithFilters(
      {
        ...filters,
        start_date,
        end_date,
        date_mode,
      },
      Boolean(search.start_date && search.end_date) || Boolean(search.date_mode)
    )
  }

  const applySourceSystem = (sourceSystem: string) => {
    navigateWithFilters({
      ...filters,
      source_system: sourceSystem === 'all' ? null : sourceSystem,
    })
  }

  const handleExport = async () => {
    try {
      const response = await exportMutation.mutateAsync({ organizationId: orgId, filters })
      downloadExportedReport(
        response,
        `incoming-wire-orders-${filters.start_date}-to-${filters.end_date}.xlsx`
      )
    } catch (error) {
      toast.error(
        await getExportErrorMessage(error, 'Unable to export incoming wire orders right now.')
      )
    }
  }

  return (
    <div className="space-y-4 pb-6">
      <AdminPageHeader
        icon={Network}
        title="Incoming Wire Orders"
        description="Expected income from FSN F2F and Condolences orders. Check those systems to confirm the amounts."
        className="mt-2"
        actions={
          <>
            <ReportDateRangeFilterBar
              appliedDateMode={REPORT_DATE_MODE.CUSTOM}
              initialFilters={{ ...filters, ...datePickerBounds }}
              now={reportNow}
              onApply={applyFilters}
            />
            <Select value={filters.source_system ?? 'all'} onValueChange={applySourceSystem}>
              <SelectTrigger aria-label="Source" className="w-[190px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  <ExternalOrdersFilterOption icon={Network} label="All sources" />
                </SelectItem>
                <SelectItem value="fsn_network">
                  <ExternalOrdersFilterOption
                    icon={FsnNetworkIcon}
                    iconClassName="scale-100"
                    label="Flower Shop Network"
                  />
                </SelectItem>
                <SelectItem value="condolences">
                  <ExternalOrdersFilterOption
                    icon={CondolencesIcon}
                    iconClassName="scale-100"
                    label="Condolences"
                  />
                </SelectItem>
              </SelectContent>
            </Select>
            <AdminHeaderActionButton
              type="button"
              variant="outline"
              onClick={handleExport}
              disabled={rows.length === 0 || exportMutation.isPending}
            >
              <Download className="h-4 w-4" />
              Export Spreadsheet
            </AdminHeaderActionButton>
          </>
        }
      />

      <SummaryCards summary={report?.summary} />
      <ExternalOrdersSourceMixCards summary={report?.summary} />
      <RowsTable
        rows={rows}
        integrationsLink={integrationsLink}
        hasExternalOrderHistory={Boolean(report?.has_external_order_history)}
      />
    </div>
  )
}

function ExternalOrdersFilterOption({
  icon: Icon,
  iconClassName,
  label,
}: {
  icon: SourceIcon
  iconClassName?: string
  label: string
}) {
  return (
    <span className="inline-flex min-w-0 items-center gap-2">
      <Icon className={cn('size-4 shrink-0', iconClassName)} aria-hidden="true" />
      <span className="truncate">{label}</span>
    </span>
  )
}

type ExternalOrdersSummaryCard = Pick<
  InfoCardProps,
  'icon' | 'tone' | 'title' | 'description' | 'value'
> & {
  key: string
}

function SummaryCards({ summary }: { summary: ExternalOrdersReportSummary | undefined }) {
  const cards: ExternalOrdersSummaryCard[] = [
    {
      key: 'external_orders',
      title: 'Incoming orders',
      description: 'Orders placed in this date range',
      value: summary?.order_count ?? 0,
      icon: Inbox,
      tone: 'periwinkle',
    },
    {
      key: 'florist_payout',
      title: 'Florist total',
      description: 'Your shop’s income from incoming orders, after changes and cancellations',
      value: money(summary?.florist_total),
      icon: HandCoins,
      tone: 'mint',
    },
  ]

  return (
    <div className="@container space-y-2">
      <div className="grid gap-4 @xl:grid-cols-2">
        {cards.map((card) => (
          <InfoCard
            key={card.key}
            icon={card.icon}
            tone={card.tone}
            title={card.title}
            description={card.description}
            value={card.value}
          />
        ))}
      </div>
    </div>
  )
}

function ExternalOrdersSourceMixCards({
  summary,
}: {
  summary: ExternalOrdersReportSummary | undefined
}) {
  const sources = summary?.sources ?? []
  const orderRows = buildSourceBreakdownRows(sources, (source) => source.order_count)
  const payoutRows = buildSourceBreakdownRows(sources, (source) =>
    parseCurrency(source.florist_total)
  )
  const orderCount = orderRows.reduce((total, row) => total + row.value, 0)

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <ExternalOrdersSourceMixCard
        title="Orders by source"
        description="Count of incoming wire orders by source"
        rows={orderRows}
        centerValue={String(orderCount)}
        centerLabel={orderCount === 1 ? 'Order' : 'Orders'}
        valueLabel="Orders"
        emptyLabel="No orders to chart"
        formatValue={(value) => formatOrderCount(value)}
      />
      <ExternalOrdersSourceMixCard
        title="Florist total by source"
        description="Your shop’s income from incoming orders"
        rows={payoutRows}
        // The server's net total: a source that nets to a cancellation has no slice but still counts.
        centerValue={money(summary?.florist_total)}
        centerLabel="Total"
        valueLabel="Florist total"
        emptyLabel="No income to chart"
        formatValue={(value) => formatCurrency(value)}
      />
    </div>
  )
}

type ExternalOrdersSourceMixCardProps = {
  title: string
  description: string
  rows: SourceBreakdownRow[]
  centerValue: string
  centerLabel: string
  valueLabel: string
  emptyLabel: string
  formatValue: FormatSourceValue
  /** Shown when there are no rows to chart. */
  emptyMessage?: string
}

function ExternalOrdersSourceMixCard({
  title,
  description,
  rows,
  centerValue,
  centerLabel,
  valueLabel,
  emptyLabel,
  formatValue,
  emptyMessage,
}: ExternalOrdersSourceMixCardProps) {
  return (
    <Card className="min-w-0 overflow-hidden">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {rows.length ? (
          <div className="grid gap-4 md:grid-cols-[160px_minmax(0,1fr)]">
            <ExternalOrdersSourcePieChart
              rows={rows}
              centerValue={centerValue}
              centerLabel={centerLabel}
              valueLabel={valueLabel}
              emptyLabel={emptyLabel}
              formatValue={formatValue}
            />
            <div className="space-y-3">
              {rows.map((row) => (
                <ExternalOrdersSourceLegendRow
                  key={row.sourceSystem}
                  row={row}
                  valueLabel={valueLabel}
                  formatValue={formatValue}
                />
              ))}
            </div>
          </div>
        ) : (
          <Typography variant="supporting">
            {emptyMessage ?? 'No data in the selected range.'}
          </Typography>
        )}
      </CardContent>
    </Card>
  )
}

function ExternalOrdersSourcePieChart({
  rows,
  centerValue,
  centerLabel,
  valueLabel,
  emptyLabel,
  formatValue,
}: Omit<ExternalOrdersSourceMixCardProps, 'title' | 'description' | 'emptyMessage'>) {
  if (!rows.length) {
    return <EmptyState size="compact" surface="transparent" title={emptyLabel} />
  }

  const chartConfig: ChartConfig = Object.fromEntries(
    rows.map((row) => [
      row.chartKey,
      {
        label: row.label,
        color: row.color,
      },
    ])
  )

  return (
    <div className="flex flex-col items-center justify-center">
      <ChartContainer config={chartConfig} className="aspect-square h-[150px] w-full max-w-[150px]">
        <PieChart>
          <ChartTooltip
            cursor={false}
            content={
              <ChartTooltipContent
                hideLabel
                formatter={(_value, _name, _item, _index, slice) => {
                  // Recharts hands back the hovered slice's data row untyped; find it by its key.
                  const row = rows.find(
                    (candidate) => candidate.chartKey === readProperty(slice, 'chartKey')
                  )

                  return row ? (
                    <div className="grid w-full grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1">
                      <span>{row.label}</span>
                      <span className="font-medium tabular-nums text-foreground">
                        {formatPieSharePercent(row.share)}
                      </span>
                      <span className="text-muted-foreground">{valueLabel}</span>
                      <span className="font-medium tabular-nums text-foreground">
                        {formatValue(row.value)}
                      </span>
                    </div>
                  ) : null
                }}
              />
            }
          />
          <Pie
            data={rows}
            dataKey="chartValue"
            nameKey="chartKey"
            isAnimationActive={false}
            innerRadius={42}
            outerRadius={62}
            paddingAngle={2}
            strokeWidth={2}
          >
            <Label
              content={({ viewBox }) => (
                <ExternalOrdersPieCenterLabel
                  viewBox={viewBox}
                  value={centerValue}
                  label={centerLabel}
                />
              )}
            />
            {rows.map((row) => (
              // oxlint-disable-next-line typescript/no-deprecated -- grandfathered
              <Cell key={row.chartKey} fill={`var(--color-${row.chartKey})`} />
            ))}
          </Pie>
        </PieChart>
      </ChartContainer>
    </div>
  )
}

function ExternalOrdersPieCenterLabel({
  viewBox,
  value,
  label,
}: {
  viewBox: LabelProps['viewBox']
  value: string
  label: string
}) {
  // Only a polar (pie) view box has a center.
  if (
    !viewBox ||
    !('cx' in viewBox) ||
    typeof viewBox.cx !== 'number' ||
    typeof viewBox.cy !== 'number'
  ) {
    return null
  }

  return (
    <text x={viewBox.cx} y={viewBox.cy} textAnchor="middle" dominantBaseline="middle">
      <tspan
        x={viewBox.cx}
        y={viewBox.cy - 2}
        className="fill-foreground text-sm font-semibold tabular-nums"
      >
        {value}
      </tspan>
      <tspan x={viewBox.cx} y={viewBox.cy + 14} className="fill-muted-foreground text-[10px]">
        {label}
      </tspan>
    </text>
  )
}

function ExternalOrdersSourceLegendRow({
  row,
  valueLabel,
  formatValue,
}: {
  row: SourceBreakdownRow
  valueLabel: string
  formatValue: FormatSourceValue
}) {
  const Icon = row.Icon

  return (
    <div className="rounded-xl border border-border/60 bg-muted/20 px-4 py-3">
      <div className="flex min-w-0 items-center gap-3">
        <span
          className="h-2.5 w-2.5 shrink-0 rounded-full"
          style={{ backgroundColor: row.color }}
          aria-hidden="true"
        />
        <span className="inline-flex size-7 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border/70 bg-background">
          <Icon className={cn('size-4', row.sourceSystem === 'fsn_network' && 'size-3.5')} />
        </span>
        <div className="grid min-w-0 flex-1 grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-1">
          <span className="truncate text-sm text-foreground">{row.label}</span>
          <div className="text-right text-sm font-semibold tabular-nums text-foreground">
            {formatValue(row.value)}
          </div>
          <span className="truncate text-xs text-muted-foreground">{valueLabel}</span>
          <span className="text-right text-xs tabular-nums text-muted-foreground">
            {formatPieSharePercent(row.share)}
          </span>
        </div>
      </div>
    </div>
  )
}

/** A pie slice per source with something to show; a source that nets to nothing or less has no slice. */
function buildSourceBreakdownRows(
  sources: ExternalOrdersReportSourceTotals[],
  getValue: (source: ExternalOrdersReportSourceTotals) => number
): SourceBreakdownRow[] {
  const sourceRows: SourceTotal[] = sources
    .map((source) => ({
      sourceSystem: source.source_system,
      label: EXTERNAL_ORDER_SOURCE_META[source.source_system]?.label ?? source.provider,
      value: getValue(source),
    }))
    .filter((row) => row.value > 0)
  const total = sourceRows.reduce((sum, row) => sum + row.value, 0)

  return sourceRows
    .sort((left, right) => right.value - left.value || left.label.localeCompare(right.label))
    .map((row, index) => {
      const meta = EXTERNAL_ORDER_SOURCE_META[row.sourceSystem]

      return {
        ...row,
        chartKey: getSourceChartKey(row.sourceSystem),
        chartValue: row.value,
        share: total > 0 ? row.value / total : 0,
        color: meta?.color ?? FALLBACK_SOURCE_COLORS[index % FALLBACK_SOURCE_COLORS.length],
        Icon: meta?.Icon ?? Network,
      }
    })
}

function getSourceChartKey(sourceSystem: string) {
  return `external-${String(sourceSystem || 'unknown').replace(/[^a-zA-Z0-9_-]/g, '-')}`
}

function RowsTable({
  rows,
  integrationsLink,
  hasExternalOrderHistory,
}: {
  rows: ExternalOrdersReportRow[]
  integrationsLink: PathLink
  hasExternalOrderHistory: boolean
}) {
  return (
    <IndexDataTable
      mode="client"
      data={rows}
      columns={externalOrderColumns}
      getRowId={(row, index) =>
        `${row.source_system}-${row.external_reference}-${row.kind}-${index}`
      }
      pagination={false}
      emptyState={
        hasExternalOrderHistory ? (
          <div className="space-y-1 py-3">
            <Typography variant="h3">No incoming wire orders found.</Typography>
            <Typography variant="supporting">Try a different date range or source.</Typography>
          </div>
        ) : (
          <div className="space-y-4 py-3">
            <div className="space-y-1">
              <Typography variant="h3">No incoming wire orders yet.</Typography>
              <Typography variant="supporting">
                Flower Shop Network and Condolences activity will show here once orders are
                imported.
              </Typography>
            </div>
            <Can do="list" on="condolences">
              <Button asChild variant="outline">
                <Link {...integrationsLink}>
                  <Settings className="h-4 w-4" />
                  Manage integrations
                </Link>
              </Button>
            </Can>
          </div>
        )
      }
    />
  )
}

function SalesOrderLink({ salesOrderId }: { salesOrderId: number | null }) {
  const path = usePathHelper()
  const { orgId } = Route.useParams()

  if (!salesOrderId) return <Typography variant="supporting">—</Typography>

  return (
    <Typography
      as={Link}
      variant="label"
      tone="interaction"
      interaction="hoverUnderline"
      {...path.salesOrders.show(salesOrderId).using({ orgId }).link}
    >
      #{salesOrderId}
    </Typography>
  )
}

const externalOrderColumns: ColumnDef<ExternalOrdersReportRow>[] = [
  {
    accessorKey: 'source_system',
    header: 'Source',
    meta: {
      label: 'Source',
      kind: 'badge',
    },
    cell: ({ row }) => (
      <SourceBadge sourceSystem={row.original.source_system} provider={row.original.provider} />
    ),
  },
  {
    accessorKey: 'activity_label',
    header: 'Activity',
    meta: {
      label: 'Activity',
      kind: 'primary',
    },
  },
  {
    accessorKey: 'sales_order_id',
    header: 'Order',
    meta: {
      label: 'Order',
      kind: 'id',
    },
    cell: ({ row }) => <SalesOrderLink salesOrderId={row.original.sales_order_id} />,
  },
  {
    accessorKey: 'external_reference',
    header: 'Wire Reference',
    meta: {
      label: 'Wire Reference',
      kind: 'id',
    },
  },
  {
    accessorKey: 'status',
    header: 'Status',
    meta: {
      label: 'Status',
      kind: 'badge',
    },
    cell: ({ row }) => externalOrderStatusLabel(row.original),
  },
  {
    accessorKey: 'activity_date',
    header: 'Date',
    meta: {
      label: 'Date',
      kind: 'date',
    },
  },
  {
    accessorKey: 'delivery_date',
    header: 'Delivery Date',
    meta: {
      label: 'Delivery Date',
      kind: 'date',
    },
  },
  {
    accessorKey: 'recipient_name',
    header: 'Recipient',
    meta: {
      label: 'Recipient',
      kind: 'primary',
    },
  },
  {
    accessorKey: 'customer_paid_total',
    header: 'Customer Total',
    meta: {
      label: 'Customer Total',
      kind: 'money',
      align: 'end',
    },
    cell: ({ row }) => money(row.original.customer_paid_total),
  },
  {
    accessorKey: 'order_value',
    header: 'FSN Order Value',
    meta: {
      label: 'FSN Order Value',
      kind: 'money',
      align: 'end',
    },
    cell: ({ row }) => money(row.original.order_value),
  },
  {
    accessorKey: 'florist_total',
    header: 'Florist Total',
    meta: { label: 'Florist Total', kind: 'money', align: 'end' },
    cell: ({ row }) => money(row.original.florist_total),
  },
]

function SourceBadge({
  sourceSystem,
  provider,
}: {
  sourceSystem: ExternalOrdersReportRow['source_system']
  provider: ExternalOrdersReportRow['provider']
}) {
  const Icon = sourceSystem === 'condolences' ? CondolencesIcon : FsnNetworkIcon

  return (
    <span className="inline-flex items-center gap-2 whitespace-nowrap">
      <span className="inline-flex size-6 items-center justify-center overflow-hidden rounded-md border border-border/70 bg-background">
        <Icon className={cn('size-4', sourceSystem === 'fsn_network' && 'size-3.5')} />
      </span>
      {provider ?? sourceSystem}
    </span>
  )
}

function externalOrderStatusLabel(row: ExternalOrdersReportRow) {
  return row.status_label || unslugify(row.status)
}

function getReportFilters(
  search: ExternalOrdersReportSearch,
  organizationTimezone: string
): ExternalOrdersReportFilters {
  const { start_date, end_date, date_mode } = getCurrentReportDateFilters(
    getOrganizationNow(organizationTimezone)
  )
  const defaultFilters = { start_date, end_date, date_mode }

  return getExternalOrdersReportFiltersFromSearch(search, defaultFilters)
}

function money(value: string | null | undefined) {
  if (value === null || typeof value === 'undefined' || value === '') {
    return '—'
  }

  return formatCurrency(value)
}

function formatOrderCount(value: number) {
  return `${Number(value).toLocaleString()} ${Number(value) === 1 ? 'order' : 'orders'}`
}
